import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { Form, InputNumber, Radio, RadioChangeEvent } from "antd";
import {
    Control,
    FieldPathByValue,
    FieldValues,
    useController,
} from "react-hook-form";
import { z } from "zod";

import { ExtendedF64Number } from "api/generated/model";

import * as theme from "../theme.css";

const NEG_INFINITY_VARIANT = "NegInfinity";
const NUMBER_VARIANT = "Number";

export const numberOrInfinitySchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("NegInfinity"),
    }),
    z.object({
        type: z.literal("Number"),
        value: z.number(),
    }),
]);
export type NumberOfInfinity = z.infer<typeof numberOrInfinitySchema>;

interface RangeMinProps<TFieldsValues extends FieldValues, TPath> {
    name: TPath;
    control: Control<TFieldsValues>;
}

export function RangeMin<
    TFieldsValues extends FieldValues,
    TPath extends FieldPathByValue<TFieldsValues, NumberOfInfinity>,
>({ name, control }: RangeMinProps<TFieldsValues, TPath>) {
    const { field, fieldState } = useController({ name, control });
    const fieldValue = field.value;
    const onChange = (newValue: ExtendedF64Number) => {
        field.onChange(newValue);
        field.onBlur();
    };
    const onChangeRadio = (newValue: RadioChangeEvent) => {
        switch (newValue.target.value) {
            case NEG_INFINITY_VARIANT:
                onChange({ type: "NegInfinity" });
                break;
            case NUMBER_VARIANT:
                onChange({ type: "Number", value: 0 });
                break;
            default:
                throw new Error("Unknown radio value " + newValue.target.value);
        }
    };
    const errors =
        fieldState.error !== undefined
            ? [<p className={theme.errorText}>{fieldState.error.message}</p>]
            : [];
    return (
        <>
            <Radio.Group
                onChange={onChangeRadio}
                value={fieldValue.type}
                className={
                    fieldState.error !== undefined ? ERRORED_CLASSNAME : ""
                }
            >
                <Radio value={NEG_INFINITY_VARIANT}>-Infinity</Radio>
                <span>
                    <Radio value={NUMBER_VARIANT}>Number</Radio>
                    {fieldValue.type === "Number" && (
                        <InputNumber
                            value={fieldValue.value}
                            onChange={(n) => {
                                onChange({ type: "Number", value: n ?? 0 });
                            }}
                            onBlur={() => {
                                field.onBlur();
                            }}
                        />
                    )}
                </span>
            </Radio.Group>
            <Form.ErrorList errors={errors} />
        </>
    );
}
